import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { mapErrorToUserFriendly } from "@/application/errors";
import { useBeneficiaryDropdown } from "@/application/workflows/beneficiaries";
import type {
  InvestmentForm,
  UseInvestmentFormProps,
} from "@/application/workflows/investments/investmentForm.types";
import { useInvestmentFormSetup } from "@/application/workflows/investments/useInvestmentFormSetup";
import { useInvestmentSubmit } from "@/application/workflows/investments/useInvestmentSubmit";
import type { InvestorDropdownOption } from "@/application/workflows/investors";
import type { FiscalQuota } from "@/domain/beneficiary";
import type { InvestmentCapacityPreview } from "@/domain/fiscal/investmentCapital";
import type { SelectOption } from "../config/investmentFormOptions";
import { useInvestmentDropdownOptions } from "./useInvestmentDropdownOptions";
import { useInvestorDropdownState } from "./useInvestorDropdownState";

export type { UseInvestmentFormProps };

export interface UseInvestmentFormReturn {
  form: InvestmentForm;
  resetForm: () => void;
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
  isEditMode: boolean;
  isCorrectionMode: boolean;
  selectedQuota: FiscalQuota | null;
  availableFiscalQuotas: FiscalQuota[];
  capacity: InvestmentCapacityPreview | null;
  beneficiaryDetails: {
    fiscal_quotas: FiscalQuota[];
    incentive_type: "film_audiovisual" | "performing_arts_music" | null;
  } | null;
  projectOptions: SelectOption[];
  quotaOptions: SelectOption[];
  investorOptions: InvestorDropdownOption[];
  loadingBeneficiaryDetails: boolean;
  loadingBeneficiariesDropdown: boolean;
  loadingMoreBeneficiaries: boolean;
  hasMoreBeneficiaries: boolean;
  handleBeneficiarySearch: (query: string) => void;
  handleLoadMoreBeneficiaries: () => void;
  loadingInvestorsDropdown: boolean;
  loadingMoreInvestors: boolean;
  hasMoreInvestors: boolean;
  handleInvestorSearch: (query: string) => void;
  handleLoadMoreInvestors: () => void;
  handleInvestorChange: (investorId: string) => void;
}

export const useInvestmentForm = (props: UseInvestmentFormProps): UseInvestmentFormReturn => {
  const { t } = useTranslation();
  const { isOpen, preselectedInvestor = null, existingInvestment = null } = props;
  const messages = useMemo(
    () => ({ loadingPromoterDetails: t("investments.errors.loading_promoter_details") }),
    [t],
  );

  const setup = useInvestmentFormSetup(props, messages);
  const beneficiaryDropdown = useBeneficiaryDropdown({
    enabled: isOpen,
    onlyWithQuota: true,
    pageSize: 20,
  });
  const investorDropdown = useInvestorDropdownState({
    isOpen,
    preselectedInvestor,
    existingInvestment,
    setSelectedInvestor: setup.setSelectedInvestor,
  });

  const { projectOptions, quotaOptions } = useInvestmentDropdownOptions({
    beneficiaryOptions: beneficiaryDropdown.options,
    availableFiscalQuotas: setup.availableFiscalQuotas,
    incentiveType: setup.beneficiaryDetails?.incentive_type ?? null,
  });

  const { onSubmit, isSubmitting } = useInvestmentSubmit({
    form: setup.form,
    isEditMode: setup.isEditMode,
    existingInvestment,
    proposalUuid: props.proposalUuid ?? null,
    selectedInvestor: setup.selectedInvestor,
    selectedQuota: setup.selectedQuota,
    onInvestmentAdded: props.onInvestmentAdded,
    onClose: props.onClose,
    resetForm: setup.resetForm,
    describeError: (error) => mapErrorToUserFriendly(error, t).message,
  });

  return {
    form: setup.form,
    resetForm: setup.resetForm,
    onSubmit,
    isSubmitting,
    isEditMode: setup.isEditMode,
    isCorrectionMode: setup.isCorrectionMode,
    selectedQuota: setup.selectedQuota,
    availableFiscalQuotas: setup.availableFiscalQuotas,
    capacity: setup.capacity,
    beneficiaryDetails: setup.beneficiaryDetails,
    projectOptions,
    quotaOptions,
    loadingBeneficiaryDetails: setup.loadingBeneficiaryDetails,
    loadingBeneficiariesDropdown: beneficiaryDropdown.loading,
    loadingMoreBeneficiaries: beneficiaryDropdown.loadingMore,
    hasMoreBeneficiaries: beneficiaryDropdown.hasMore,
    handleBeneficiarySearch: beneficiaryDropdown.handleSearch,
    handleLoadMoreBeneficiaries: beneficiaryDropdown.handleLoadMore,
    ...investorDropdown,
  };
};
